import { alpha, createTheme } from '@mui/material/styles';
import { zhCN } from '@mui/material/locale';

const INK = '#183241';
const MUTED_INK = '#516775';
const SURFACE = '#fcfdfb';
const SURFACE_MUTED = '#f3f7f4';
const LINE = '#dbe5df';
const ARGUS_BLUE = '#285f74';
const IRIS_TEAL = '#28756f';
const SUCCESS = '#2f6f4f';
const WARNING = '#9a620f';
const ERROR = '#b3261e';

const fontStack = "'Noto Sans SC', 'Source Han Sans SC', 'Microsoft YaHei', 'Segoe UI', sans-serif";
const monoStack = "'SFMono-Regular', 'Cascadia Code', 'Roboto Mono', Consolas, monospace";

export const theme = createTheme(
  {
    palette: {
      mode: 'light',
      primary: {
        main: ARGUS_BLUE,
        light: '#3d788d',
        dark: '#1d4859',
      },
      secondary: {
        main: IRIS_TEAL,
        light: '#3d8d85',
        dark: '#1d5752',
      },
      success: {
        main: SUCCESS,
      },
      warning: {
        main: WARNING,
      },
      error: {
        main: ERROR,
      },
      background: {
        default: SURFACE,
        paper: '#fffefa',
      },
      text: {
        primary: INK,
        secondary: MUTED_INK,
      },
      divider: alpha(INK, 0.1),
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: fontStack,
      h1: { fontWeight: 760, letterSpacing: 0, lineHeight: 1.08 },
      h2: { fontWeight: 760, letterSpacing: 0, lineHeight: 1.1 },
      h3: { fontWeight: 740, letterSpacing: 0, lineHeight: 1.16 },
      h4: { fontWeight: 720, letterSpacing: 0, lineHeight: 1.18 },
      h5: { fontWeight: 700, letterSpacing: 0, lineHeight: 1.25 },
      h6: { fontWeight: 700, letterSpacing: 0, lineHeight: 1.3 },
      subtitle1: { fontWeight: 680, letterSpacing: 0 },
      subtitle2: { fontWeight: 680, letterSpacing: 0 },
      button: { fontWeight: 680, letterSpacing: 0, textTransform: 'none' },
      caption: { letterSpacing: 0 },
      overline: { letterSpacing: 0, textTransform: 'none', fontWeight: 700 },
      body1: { lineHeight: 1.65 },
      body2: { lineHeight: 1.65 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            boxSizing: 'border-box',
          },
          body: {
            minWidth: 320,
            backgroundColor: SURFACE,
            backgroundImage: `linear-gradient(180deg, ${SURFACE} 0%, ${SURFACE_MUTED} 100%)`,
            color: INK,
            fontFeatureSettings: '"kern"',
            textRendering: 'optimizeLegibility',
          },
          a: {
            color: 'inherit',
          },
          '::selection': {
            background: alpha(ARGUS_BLUE, 0.16),
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              scrollBehavior: 'auto !important',
              transitionDuration: '0.01ms !important',
            },
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: 'lg',
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: 'transparent',
        },
        styleOverrides: {
          root: {
            color: INK,
            backgroundColor: alpha('#fffefa', 0.94),
            borderBottom: `1px solid ${LINE}`,
            backdropFilter: 'blur(10px)',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: 68,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            minHeight: 40,
            borderRadius: 6,
            paddingInline: 18,
            paddingBlock: 8,
            transition:
              'background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
            '&:focus-visible': {
              outline: `3px solid ${alpha(ARGUS_BLUE, 0.22)}`,
              outlineOffset: 2,
            },
          },
          sizeLarge: {
            minHeight: 46,
            paddingInline: 22,
            paddingBlock: 10,
            fontSize: '0.98rem',
          },
          contained: {
            boxShadow: `0 1px 0 ${alpha(INK, 0.08)}`,
            '&:hover': {
              boxShadow: `0 8px 18px ${alpha(ARGUS_BLUE, 0.13)}`,
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderColor: alpha(INK, 0.18),
            backgroundColor: alpha('#fffefa', 0.58),
            '&:hover': {
              borderColor: alpha(ARGUS_BLUE, 0.36),
              backgroundColor: alpha(ARGUS_BLUE, 0.05),
            },
          },
          text: {
            '&:hover': {
              backgroundColor: alpha(ARGUS_BLUE, 0.06),
            },
          },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: {
            borderRadius: 8,
            borderColor: LINE,
            backgroundImage: 'none',
            boxShadow: `0 1px 2px ${alpha(INK, 0.04)}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: LINE,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: '#fffefa',
            transition: 'background-color 160ms ease, border-color 160ms ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(ARGUS_BLUE, 0.38),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: ARGUS_BLUE,
              borderWidth: 1,
            },
          },
          notchedOutline: {
            borderColor: alpha(INK, 0.16),
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: MUTED_INK,
            '&.Mui-focused': {
              color: ARGUS_BLUE,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            '&:focus-visible': {
              outline: `3px solid ${alpha(ARGUS_BLUE, 0.2)}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${LINE}`,
            boxShadow: `0 16px 40px ${alpha(INK, 0.11)}`,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: '1px solid',
            alignItems: 'center',
          },
          standardSuccess: {
            backgroundColor: alpha(SUCCESS, 0.08),
            borderColor: alpha(SUCCESS, 0.18),
            color: '#244e3a',
          },
          standardInfo: {
            backgroundColor: alpha(ARGUS_BLUE, 0.08),
            borderColor: alpha(ARGUS_BLUE, 0.18),
            color: '#214f61',
          },
          standardWarning: {
            backgroundColor: alpha(WARNING, 0.08),
            borderColor: alpha(WARNING, 0.2),
            color: '#6f470f',
          },
          standardError: {
            backgroundColor: alpha(ERROR, 0.08),
            borderColor: alpha(ERROR, 0.2),
            color: '#7e211d',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontWeight: 680,
          },
          outlined: {
            borderColor: alpha(INK, 0.16),
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: LINE,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: alpha(INK, 0.1),
          },
          head: {
            color: INK,
            fontWeight: 720,
            backgroundColor: alpha(ARGUS_BLUE, 0.05),
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: alpha(INK, 0.22),
            '&.Mui-active': {
              color: ARGUS_BLUE,
            },
            '&.Mui-completed': {
              color: SUCCESS,
            },
          },
        },
      },
    },
  },
  zhCN,
);

export const runnerTheme = createTheme(
  {
    palette: {
      mode: 'dark',
      primary: {
        main: '#7fc9dd',
      },
      secondary: {
        main: '#86d0c8',
      },
      success: {
        main: '#74c69d',
      },
      warning: {
        main: '#f0b65a',
      },
      error: {
        main: '#f18a84',
      },
      background: {
        default: '#080d12',
        paper: '#101820',
      },
      divider: alpha('#f2f8f5', 0.12),
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: fontStack,
      h4: { fontWeight: 760, letterSpacing: 0, lineHeight: 1.12 },
      h5: { fontWeight: 720, letterSpacing: 0, lineHeight: 1.18 },
      h6: { fontWeight: 700, letterSpacing: 0, lineHeight: 1.25 },
      button: { fontWeight: 680, letterSpacing: 0, textTransform: 'none' },
      body1: { lineHeight: 1.65 },
      body2: { lineHeight: 1.65 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: '#080d12',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            minHeight: 42,
            borderRadius: 6,
            paddingInline: 18,
            paddingBlock: 8,
          },
          sizeLarge: {
            minHeight: 48,
            paddingInline: 24,
            paddingBlock: 10,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  },
  zhCN,
);

export const productTokens = {
  ink: INK,
  mutedInk: MUTED_INK,
  surface: SURFACE,
  surfaceMuted: SURFACE_MUTED,
  line: LINE,
  argus: ARGUS_BLUE,
  iris: IRIS_TEAL,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  fontStack,
  monoStack,
};

export default theme;
